import React, { useState } from "react";
import { Sparkles, Check, Calendar, ArrowRight } from "lucide-react";

const CALENDLY_URL = "https://calendly.com/abdelvip";

/** Step 0 — Welcome: book a live expert demo via Calendly, or start solo */
export function StepWelcome({ onContinueSolo }) {
  const [booked, setBooked] = useState(false);

  const openCalendly = () => {
    if (typeof window !== "undefined" && window.Calendly?.initPopupWidget) {
      window.Calendly.initPopupWidget({
        url: CALENDLY_URL,
        prefill: {},
        utm: { utmSource: "prosperr-onboarding" },
      });
      setBooked(true);
      return false;
    }
    window.open(CALENDLY_URL, "_blank", "noopener,noreferrer");
    setBooked(true);
  };

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--text)] flex flex-col relative overflow-hidden" data-testid="onboarding-step-0">
      <div className="brand-glow absolute inset-0 pointer-events-none" />
      <div className="px-6 pt-10 pb-2 flex items-center gap-2 relative">
        <div className="w-7 h-7 bg-emerald-500 rounded-lg grid place-items-center">
          <Sparkles className="w-3.5 h-3.5 text-white" strokeWidth={1.8} />
        </div>
        <span className="font-display font-semibold text-base">Prosperr</span>
      </div>

      <div className="px-6 pt-6 relative">
        <h1 className="font-display text-4xl sm:text-5xl font-semibold text-[var(--text)] leading-[1.02] tracking-tight">
          Bienvenue sur Prosperr.<br />
          <span className="text-zinc-500">Configurons votre assistant commercial.</span>
        </h1>
        <p className="text-sm text-zinc-400 mt-3 leading-relaxed">
          Prosperr va préparer votre analyse commerciale personnalisée. Vous validez chaque étape avant que nous lancions quoi que ce soit.
        </p>
      </div>

      <div className="px-6 mt-7 space-y-3 flex-1 relative">
        {/* Calendly demo */}
        <div className="hp-card p-5 relative overflow-hidden" data-testid="demo-calendly-option">
          <div
            className="absolute inset-0 pointer-events-none opacity-70"
            style={{ background: "radial-gradient(circle at 10% 10%, rgba(0,220,130,0.2), transparent 50%)" }}
          />
          <div className="relative">
            <div className="inline-flex items-center gap-1.5 hp-label bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 px-2.5 py-1 rounded-full mb-3">
              Recommandé
            </div>
            <p className="text-lg font-semibold text-[var(--text)] leading-tight">
              Réserver une démo live avec un expert
            </p>
            <p className="text-sm text-zinc-400 mt-2 leading-relaxed">
              20 minutes de visioconférence avec Abdel. Il configure Prosperr sur votre secteur d'activité en direct et vous montre comment doubler vos rendez-vous commerciaux qualifiés.
            </p>
            {!booked ? (
              <button
                onClick={openCalendly}
                data-testid="open-calendly-btn"
                className="hp-btn hp-btn-primary w-full h-11 mt-4"
              >
                <Calendar className="w-4 h-4" />
                Choisir mon créneau
              </button>
            ) : (
              <div className="mt-4 bg-[var(--surface-alt)] rounded-lg p-4 border border-[var(--border)]">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-6 h-6 rounded-full bg-emerald-600 grid place-items-center shrink-0">
                    <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[var(--text)]">Démo programmée</p>
                    <p className="text-xs text-zinc-500">
                      Confirmation envoyée par email. Continuez votre analyse en parallèle.
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={openCalendly}
                    className="hp-btn hp-btn-secondary flex-1 h-9 text-xs"
                    data-testid="reopen-calendly-btn"
                  >
                    Modifier
                  </button>
                  <button
                    onClick={onContinueSolo}
                    data-testid="continue-after-calendly"
                    className="hp-btn hp-btn-primary flex-1 h-9 text-xs"
                  >
                    Continuer →
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Solo analysis */}
        <button
          onClick={onContinueSolo}
          data-testid="self-config-btn"
          className="w-full hp-card px-5 py-4 text-left hover:border-zinc-700 active:scale-[0.99] transition-colors group flex items-center justify-between gap-3"
        >
          <div>
            <p className="font-semibold text-[var(--text)] text-sm group-hover:text-emerald-300 transition-colors">
              Démarrer mon analyse en autonomie
            </p>
            <p className="text-xs text-zinc-500 mt-0.5">
              3 étapes guidées · environ 10 minutes · vous validez chaque résultat
            </p>
          </div>
          <ArrowRight className="w-4 h-4 text-zinc-500 group-hover:text-emerald-400 shrink-0 transition-colors" />
        </button>

        <p className="text-center text-[11px] text-zinc-600 pb-8 pt-2">
          Prosperr vous présente son analyse avant toute action.
        </p>
      </div>
    </div>
  );
}
